<script setup lang="ts">
import { computedAsync } from '@vueuse/core';
import { computed, nextTick, ref, watch } from 'vue';
import type { LngLatBounds, Map as MapboxMap } from 'mapbox-gl';

import {
  useBrowserStore,
  useEditingIngredientStore,
  useMapStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import {
  ProductSelectionMapLayout,
  ProductSelectionMap,
  MAPBOX_STYLE,
} from '@apps/main/new-desktop/modules/newMaps';
import type { MapboxStyleType } from '@apps/main/new-desktop/modules/newMaps';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';
import { useHotelFiltersStore } from '@apps/main/new-desktop/modules/hotelFilters/store/hotelFiltersStore';

import { AppFlicker } from '@libs/ui';
import type { IngredientId } from '@libs/trip-planner-interfaces';
import type { Rectangle } from '@libs/maps';
import { isMultiExperimentAsync } from '@libs/feature-toggle';

import { HotelSelectionModalType } from '../models';
import {
  useHotelSelectionMapStore,
  useHotelSelectionModalsStore,
  useHotelSelectionStore,
} from '../stores';

import HotelMapCard from './HotelMapCard.vue';

interface HotelSelectionMapEmits {
  (eventName: 'open-filters'): void;
  (eventName: 'close-map'): void;
}

const emit = defineEmits<HotelSelectionMapEmits>();

const browserStore = useBrowserStore();
const hotelSelectionStore = useHotelSelectionStore();
const hotelSelectionMapStore = useHotelSelectionMapStore();
const statsigEventsStore = useStatsigEventsStore();
const hotelSelectionModalsStore = useHotelSelectionModalsStore();
const mapStore = useMapStore();
const hotelFiltersStore = useHotelFiltersStore();
const filtersStore = useFiltersStore();
const editingIngredientStore = useEditingIngredientStore();

// State
const shouldShowSearchThisAreaButton = ref<boolean>(false);
const initialLoadDone = ref<boolean>(false);
const mapboxStyleType = ref<MapboxStyleType>('streets');

const showMapbox = computed<boolean>(
  () => initialLoadDone.value || !hotelSelectionStore.shouldDisplayLoader
);

const productMapContentExperiment = computedAsync<string>(
  () => isMultiExperimentAsync('product_map_content'),
  'NOT_LOADED'
);

const mapStyles = computed<string | undefined>(() => {
  if (productMapContentExperiment.value === 'landmarks') {
    return 'mapbox://styles/marcos-pastor/cmienklva00n301s9aiymefoh';
  }

  if (productMapContentExperiment.value === 'landmarks_pois') {
    return 'mapbox://styles/marcos-pastor/cmig8ow8e004001qwgxo5azfa';
  }

  if (productMapContentExperiment.value === 'none') {
    return MAPBOX_STYLE.streets;
  }

  return undefined;
});

function onExposeMapInstance(map: MapboxMap): void {
  hotelSelectionMapStore.mapRef = map;
  initialLoadDone.value = true;
}

function showSearchThisAreaButton(): void {
  shouldShowSearchThisAreaButton.value = true;
}

function boundsToRectangle(bounds: LngLatBounds): Rectangle {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();

  return {
    topLeft: {
      latitude: ne.lat,
      longitude: sw.lng,
    },
    bottomRight: {
      latitude: sw.lat,
      longitude: ne.lng,
    },
  };
}

function onSearchThisArea(): void {
  shouldShowSearchThisAreaButton.value = false;
  hotelSelectionMapStore.savedBounds = getCurrentBounds();

  if (hotelSelectionMapStore.savedBounds) {
    const event = {
      newView: boundsToRectangle(hotelSelectionMapStore.savedBounds),
      userInitiated: true,
    };

    if (editingIngredientStore.selectedNeigborhoodFilters.length) {
      clearNeighborhoodPolygonsFiltersSelection();
      setTimeout(() => {
        mapStore.setMapMovedAndCreateNewPolygon(event);
      }, 0);
    } else {
      mapStore.setMapMovedAndCreateNewPolygon(event);
    }

    statsigEventsStore.logHotelSelectionSearchThisAreaClicked();
  }
}

function clearNeighborhoodPolygonsFiltersSelection(): void {
  hotelFiltersStore.clearNeighborhoodPolygonsFilters();
  filtersStore.clearNeighborhoodPolygonsFilters();
}

function getCurrentBounds(): LngLatBounds | undefined {
  if (!hotelSelectionMapStore.mapRef) {
    return undefined;
  }
  return hotelSelectionMapStore.mapRef.getBounds() ?? undefined;
}

let removeBoundsButtonEl: HTMLButtonElement | null = null;

async function onToggleFullScreen(): Promise<void> {
  if (!hotelSelectionMapStore.mapRef) {
    return;
  }

  if (hotelSelectionMapStore.isFullScreen) {
    statsigEventsStore.logHotelSelectionCollapseMap();
  } else {
    statsigEventsStore.logHotelSelectionExtendMap();
  }

  hotelSelectionMapStore.onToggleFullScreen(
    !hotelSelectionMapStore.isFullScreen
  );
  await nextTick();
  hotelSelectionMapStore.mapRef?.resize();
}

async function onHotelCardClicked(): Promise<void> {
  statsigEventsStore.logHotelSelectionMapCardClicked({
    mapSize: hotelSelectionMapStore.hotelMapCardType,
    hotelCode: hotelSelectionMapStore.highlightedHotel?.hotelCode || '',
  });
  await hotelSelectionMapStore.hotelCardClicked();
}

function onPeekPrice(): void {
  if (!hotelSelectionMapStore.hotelMapCardData) {
    return;
  }
  statsigEventsStore.logHotelSelectionPeekPriceModalOpened({
    source: 'mapMarker',
  });
  hotelSelectionModalsStore.openPeekPriceModal(
    hotelSelectionMapStore.hotelMapCardData.ingredientIndex,
    hotelSelectionMapStore.hotelMapCardData?.hotelCode
  );
}

function onHide(): void {
  if (
    hotelSelectionModalsStore.hotelSelectionModalType ===
    HotelSelectionModalType.PeekPrice
  ) {
    return;
  }
  hotelSelectionMapStore.hideHotelCard();
  hotelSelectionMapStore.resetInspect();
}

function onMarkerClick(data: {
  code: string;
  ingredientId: IngredientId;
}): void {
  hotelSelectionMapStore.showHotelMapCard(data.ingredientId, data.code);
  hotelSelectionMapStore.inspect(data.ingredientId, data.code);
}

function onClusterClick(): void {
  statsigEventsStore.logHotelSelectionMapClusterClicked();
}

function onSetMapboxStyle(newStyleType: MapboxStyleType): void {
  if (!mapStyles.value) {
    return;
  }

  mapboxStyleType.value = newStyleType;

  if (newStyleType === 'streets') {
    hotelSelectionMapStore.mapRef?.setStyle(mapStyles.value);
  } else {
    const style = MAPBOX_STYLE[newStyleType];
    hotelSelectionMapStore.mapRef?.setStyle(style);
  }
  statsigEventsStore.logHotelSelectionToggleMapType({ mapType: newStyleType });
}

watch(
  () => hotelSelectionMapStore.savedBounds,
  (bounds) => {
    if (!hotelSelectionMapStore.mapRef) {
      return;
    }

    if (bounds === undefined) {
      if (hotelSelectionMapStore.mapRef.getLayer('bounds-layer')) {
        hotelSelectionMapStore.mapRef.removeLayer('bounds-layer');
      }
      if (hotelSelectionMapStore.mapRef.getSource('bounds-source')) {
        hotelSelectionMapStore.mapRef.removeSource('bounds-source');
      }
      removeBoundsButtonEl?.remove();
      removeBoundsButtonEl = null;
    }
  },
  { deep: true }
);
</script>

<template>
  <ProductSelectionMapLayout
    :map-layout="browserStore.isLargerThan1199px ? 'desktop' : 'mobile'"
    :is-desktop-map-full-screen="hotelSelectionMapStore.isFullScreen"
    :should-show-search-this-area-button="shouldShowSearchThisAreaButton"
    :active-filters-count="hotelSelectionStore.activeFiltersCount"
    :should-display-loader="hotelSelectionStore.shouldDisplayLoader"
    :mapbox-style-type="mapboxStyleType"
    @toggle-full-screen="onToggleFullScreen"
    @search-this-area="onSearchThisArea"
    @mouse-leave="onHide"
    @open-filters="emit('open-filters')"
    @close-map="emit('close-map')"
    @set-mapbox-style="onSetMapboxStyle"
  >
    <template #map>
      <AppFlicker
        v-if="!initialLoadDone"
        width="100%"
        height="100%"
        border-radius="0"
      />

      <ProductSelectionMap
        v-if="showMapbox && mapStyles"
        :data="hotelSelectionMapStore.mapboxProperties"
        :should-show-zoom-controls="browserStore.isLargerThan1199px"
        :map-styles="mapStyles"
        @expose-instance="onExposeMapInstance"
        @dragend="showSearchThisAreaButton"
        @zoomend="showSearchThisAreaButton"
        @marker-click="onMarkerClick($event)"
        @cluster-click="onClusterClick()"
      />
    </template>

    <template #product-card>
      <HotelMapCard
        v-if="
          hotelSelectionMapStore.highlightedHotel?.showHotelMapCard &&
          hotelSelectionMapStore.hotelMapCardData
        "
        :data="hotelSelectionMapStore.hotelMapCardData"
        :card-type="hotelSelectionMapStore.hotelMapCardType"
        class="hotel-map-card"
        @click="onHotelCardClicked"
        @open-peek-price-calendar="onPeekPrice"
        @hide="onHide"
      />
    </template>
  </ProductSelectionMapLayout>
</template>
